import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Pencil, Trash2 } from 'lucide-react';
import { useMe } from '../hooks/useMe.js';
import { useSync } from '../hooks/useSync.js';
import { useLogout } from '../hooks/useLogin.js';
import { useBankAccounts } from '../hooks/useBankAccounts.js';
import { usePasskey } from '../hooks/usePasskey.js';
import { usePasskeyDevices, useDeletePasskey } from '../hooks/usePasskeyDevices.js';
import Button from '../components/Button.jsx';
import LoadingSpinner from '../components/LoadingSpinner.jsx';
import BankAccountRenameModal from '../components/BankAccountRenameModal.jsx';
import { formatDate, formatCurrency } from '../lib/format.js';
import { PROVIDER_LABELS } from '../lib/providers.js';
import queryClient from '../lib/queryClient.js';

// Interprète un résultat de provider tel que renvoyé par l'orchestrateur
// POST /v1/sync (routes/sync.routes.js) — la shape varie volontairement d'un
// provider à l'autre (qonto: {status, new, updated, balance_cents, ...} ;
// wise: {fetched, created, updated, ...} sans `status` du tout ; revolut:
// {status, created, updated, errors, ...}) donc cette fonction ne suppose
// jamais qu'un champ précis existe — seulement les 3 formes génériques
// possibles : skip (route ou syncFn), erreur attrapée par l'orchestrateur
// ({ error }), ou un objet de compteurs interprété comme un succès.
function summarizeSyncResult(result) {
  if (!result) return { tone: 'muted', text: '—' };

  if (result.skipped === true || result.status === 'skipped') {
    const reasonLabels = {
      no_credentials: 'Non configuré',
      already_running: 'Sync déjà en cours',
      lock_busy: 'Sync déjà en cours',
      no_token: 'Non configuré',
      no_profile: 'Profil introuvable',
    };
    return { tone: 'muted', text: reasonLabels[result.reason] ?? 'Ignoré' };
  }

  if (result.error) {
    return { tone: 'error', text: result.error };
  }

  const created = result.created ?? result.new ?? 0;
  const updated = result.updated ?? 0;
  return { tone: 'success', text: `${created} nouvelle${created !== 1 ? 's' : ''}, ${updated} mise${updated !== 1 ? 's' : ''} à jour` };
}

const SYNC_RESULT_TONE_CLASS = {
  muted: 'text-slate-500',
  error: 'text-red-600',
  success: 'text-green-600',
};

export default function SettingsPage() {
  const navigate = useNavigate();
  const { data: me, isLoading: meLoading } = useMe();
  const sync = useSync();
  const logout = useLogout();
  const { data: bankAccountsData, isLoading: accountsLoading } = useBankAccounts();
  const [confirmFull, setConfirmFull] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const passkey = usePasskey();
  const { data: passkeysData } = usePasskeyDevices();
  const deletePasskey = useDeletePasskey();
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const passkeys = passkeysData?.items ?? [];

  const accounts = Array.isArray(bankAccountsData) ? bankAccountsData : [];

  async function handleLogout() {
    try {
      await logout.mutateAsync();
    } catch {}
    queryClient.clear();
    navigate('/login', { replace: true });
  }

  async function handleDeletePasskey(id) {
    try {
      await deletePasskey.mutateAsync(id);
    } catch {}
    setConfirmDeleteId(null);
  }

  async function handleSync(full = false) {
    setConfirmFull(false);
    await sync.mutateAsync({ full });
  }

  if (meLoading) {
    return (
      <div className="flex justify-center py-12"><LoadingSpinner /></div>
    );
  }

  return (
    <div className="space-y-6 max-w-xl">
      <h1 className="text-lg font-semibold text-slate-900">Paramètres</h1>

      {/* Compte */}
      <section className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 space-y-3">
        <h2 className="text-sm font-semibold text-slate-800">Compte</h2>
        {me && (
          <dl className="space-y-1.5 text-sm">
            <div className="flex gap-3">
              <dt className="text-slate-500 w-24 shrink-0">Nom</dt>
              <dd className="text-slate-800 font-medium">
                {me.name ?? (`${me.first_name ?? ''} ${me.last_name ?? ''}`.trim() || '—')}
              </dd>
            </div>
            <div className="flex gap-3">
              <dt className="text-slate-500 w-24 shrink-0">Email</dt>
              <dd className="text-slate-800 font-medium">{me.email ?? '—'}</dd>
            </div>
            <div className="flex gap-3">
              <dt className="text-slate-500 w-24 shrink-0">2FA</dt>
              <dd>
                {me.totp_enabled
                  ? <span className="text-green-600 font-medium">Activée</span>
                  : <span className="text-orange-600 font-medium">Non configurée</span>
                }
              </dd>
            </div>
          </dl>
        )}
      </section>

      {/* Face ID / passkeys */}
      <section className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 space-y-3">
        <h2 className="text-sm font-semibold text-slate-800">Face ID / passkeys</h2>
        {passkeys.length === 0 ? (
          <p className="text-sm text-slate-500">Aucun appareil enregistré.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {passkeys.map((p) => (
              <li key={p.id} className="flex items-center justify-between gap-3 py-2.5 first:pt-0 last:pb-0">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">{p.device_label || 'Appareil'}</p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {p.last_used_at ? `Utilisé le ${formatDate(p.last_used_at)}` : 'Jamais utilisé'}
                  </p>
                </div>
                {confirmDeleteId === p.id ? (
                  <div className="flex items-center gap-2 shrink-0">
                    <Button
                      variant="danger"
                      className="text-xs px-3 py-1.5"
                      onClick={() => handleDeletePasskey(p.id)}
                      loading={deletePasskey.isPending}
                    >
                      Supprimer
                    </Button>
                    <Button
                      variant="ghost"
                      className="text-xs px-3 py-1.5"
                      onClick={() => setConfirmDeleteId(null)}
                    >
                      Annuler
                    </Button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setConfirmDeleteId(p.id)}
                    className="p-1.5 shrink-0 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-red-600 focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:outline-none transition-colors"
                    aria-label={`Supprimer l'appareil ${p.device_label || 'Appareil'}`}
                  >
                    <Trash2 size={15} />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
        {(passkey.error || deletePasskey.error) && (
          <p className="text-xs text-red-600" role="alert">
            {passkey.error ?? deletePasskey.error.message}
          </p>
        )}
        {passkey.isSupported && (
          <Button
            variant="ghost"
            onClick={() => passkey.register()}
            loading={passkey.busy}
          >
            Activer Face ID sur cet appareil
          </Button>
        )}
      </section>

      {/* Synchronisation */}
      <section className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 space-y-3">
        <h2 className="text-sm font-semibold text-slate-800">Synchronisation</h2>
        {me?.last_sync_at && (
          <p className="text-xs text-slate-500">
            Dernière synchronisation : <span className="font-medium">{formatDate(me.last_sync_at)}</span>
          </p>
        )}
        {sync.isSuccess && sync.data && (
          <ul className="space-y-1">
            {Object.entries(sync.data).map(([kind, result]) => {
              const { tone, text } = summarizeSyncResult(result);
              return (
                <li key={kind} className="flex items-center justify-between gap-3 text-xs">
                  <span className="font-medium text-slate-700">{PROVIDER_LABELS[kind] ?? kind}</span>
                  <span className={SYNC_RESULT_TONE_CLASS[tone]}>{text}</span>
                </li>
              );
            })}
          </ul>
        )}
        {sync.error && (
          <p className="text-xs text-red-600">{sync.error.message}</p>
        )}
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="ghost"
            onClick={() => handleSync(false)}
            loading={sync.isPending}
          >
            Synchroniser (incrémental)
          </Button>
          {!confirmFull ? (
            <Button
              variant="ghost"
              className="text-orange-600 border-orange-200 hover:bg-orange-50"
              onClick={() => setConfirmFull(true)}
            >
              Synchronisation complète…
            </Button>
          ) : (
            <div className="flex items-center gap-2">
              <span className="text-xs text-orange-700 font-medium">Confirmer la sync complète ?</span>
              <Button
                variant="danger"
                className="text-xs px-3 py-1.5"
                onClick={() => handleSync(true)}
                loading={sync.isPending}
              >
                Oui, lancer
              </Button>
              <Button
                variant="ghost"
                className="text-xs px-3 py-1.5"
                onClick={() => setConfirmFull(false)}
              >
                Annuler
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* Comptes bancaires */}
      <section className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 space-y-3">
        <h2 className="text-sm font-semibold text-slate-800">Comptes bancaires</h2>
        {accountsLoading ? (
          <div className="flex justify-center py-4"><LoadingSpinner /></div>
        ) : accounts.length === 0 ? (
          <p className="text-sm text-slate-500">Aucun compte bancaire synchronisé.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {accounts.map((account) => (
              <li
                key={account.id}
                className="flex items-center justify-between gap-3 py-2.5 first:pt-0 last:pb-0"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">{account.name}</p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {PROVIDER_LABELS[account.provider] ?? account.provider}
                    {account.tx_count != null && (
                      <span> · {account.tx_count} transaction{account.tx_count !== 1 ? 's' : ''}</span>
                    )}
                  </p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="text-sm tabular-nums font-medium text-slate-700">
                    {account.balance_cents != null ? formatCurrency(account.balance_cents) : '—'}
                  </span>
                  <button
                    type="button"
                    onClick={() => setEditingAccount(account)}
                    className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700 focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:outline-none transition-colors"
                    aria-label={`Renommer le compte ${account.name}`}
                  >
                    <Pencil size={15} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Déconnexion */}
      <section className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
        <Button
          variant="danger"
          onClick={handleLogout}
          loading={logout.isPending}
          className="w-full"
        >
          Déconnexion
        </Button>
      </section>

      <BankAccountRenameModal
        account={editingAccount}
        onClose={() => setEditingAccount(null)}
      />
    </div>
  );
}
